import { Feature } from "../phonemes/features";
import { ipaToFeatures, IpaType, syllableBreak } from "../phonemes/ipa";
import { Pattern } from "./pattern";
import { PhonemeInfo } from "./phonemeInfo";

/**
 * generate strings from a pattern, randomly filling in the blanks
 */
export function makeFromPattern(pattern: Pattern<PhonemeInfo>, options: IpaOptions): IpaType[] {
  if ('seq' in pattern) {
    return makeSeq(pattern.seq, options);
  } else if ('and' in pattern) {
    return [];  // unsupported
  } else if ('or' in pattern) {
    return makeOr(pattern.or, options);
  } else if ('not' in pattern) {
    return [];  // unsupported
  } else if ('opt' in pattern) {
    return makeOpt(pattern.opt, options);
  } else if ('store' in pattern) {
    return makeStore(pattern.store, options);
  } else {
    return makePhoneme(pattern, options);
  }
}

export class IpaOptions {
  /**
   * @param valid IPA that can be used to generate phonemes from a pattern
   * @param lookup used to get possible IPA for a given name in NamedItem
   */
  constructor(private readonly valid: IpaType[], private readonly lookup: Record<string, IpaType[]>) { }

  /** pick a random item from lookup[name] */
  fromLookup(name: string): IpaType[] {
    const all = this.lookup[name];
    const n = all === undefined ? 0 : all.length;
    return n === 0 ? [] : [all[randomInt(n)]];
  }

  /** generate valid IPA with the given set of features */
  withFeatures(features: Feature[]): IpaType[] {
    const all = this.findWith(features);
    const n = all.length;
    return n === 0 ? [] : [all[randomInt(n)]];
  }

  /** find all IPA from the valid set with the given set of features */
  private findWith(needs: Feature[]): IpaType[] {
    const results: IpaType[] = [];
    for (const ipa of this.valid) {
      const has = ipaToFeatures(ipa);
      if (has && needs.every(f => has.includes(f))) {
        results.push(ipa);
      }
    }
    return results;
  }
}

function makeSeq(seq: Pattern<PhonemeInfo>[], options: IpaOptions): string[] {
  return seq.flatMap(p => makeFromPattern(p, options));
}

function makeOr(or: Pattern<PhonemeInfo>[], options: IpaOptions): string[] {
  const pick = or[randomInt(or.length)];
  return makeFromPattern(pick, options);
}

function makeOpt(opt: Pattern<PhonemeInfo>, options: IpaOptions): string[] {
  return randomInt(2) === 0 ? [] : makeFromPattern(opt, options);
}

function makeStore(store: Pattern<PhonemeInfo>, options: IpaOptions): string[] {
  return makeFromPattern(store, options);
}

function makePhoneme(item: PhonemeInfo, options: IpaOptions): string[] {
  if ('feature' in item) {
    return options.withFeatures(item.feature);
  } else if ('ipa' in item) {
    return item.ipa;
  } else if ('raw' in item) {
    return item.raw;
  } else if ('special' in item) {
    return item.special === 'word' ? [' ']
      : item.special === 'syllable' ? [syllableBreak] : [];
  } else if ('name' in item) {
    return options.fromLookup(item.name);
  } else if ('empty' in item) {
    return [];  // 'empty' always returns nothing
  }
  return [];
}

/** random int in [0, n) */
function randomInt(n: number): number {
  return Math.floor(Math.random() * n);
}
